import React from 'react';
import { FiSearch, FiChevronLeft, FiChevronRight, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import './TableFilters.css';

/**
 * Cabeçalho de coluna ordenável
 */
export const SortableTh = ({ children, sortKey, currentSort, currentOrder, onSort }) => {
  if (!onSort || !sortKey) return <th>{children}</th>;
  const isActive = currentSort === sortKey;
  return (
    <th className="sortable-th" onClick={() => onSort(sortKey)}>
      {children}
      {isActive && (
        <span className="sort-icon">
          {currentOrder === 'desc' ? <FiChevronDown /> : <FiChevronUp />}
        </span>
      )}
    </th>
  );
};

const renderFilter = (f, fill) => (
  <div key={f.key} className={`filter-wrap ${fill ? 'filter-wrap-fill' : ''}`}>
    <label>{f.label}</label>
    {f.type === 'date' ? (
      <input
        type="date"
        value={f.value || ''}
        onChange={(e) => f.onChange(e.target.value)}
        className="filter-date-input"
      />
    ) : (
      <select
        value={f.value || ''}
        onChange={(e) => f.onChange(e.target.value)}
      >
        {f.options?.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    )}
  </div>
);

/**
 * Barra de filtros, busca e paginação para tabelas
 * @param {Object} props
 * @param {string} props.search - Valor da busca
 * @param {Function} props.onSearchChange - (value) => void
 * @param {Object[]} props.filters - [{ key, label, value, onChange, options: [{value, label}] }]
 * @param {string[][]} [props.filterRows] - Agrupa filtros em linhas. Ex: [['search','empreendimento'],['tipo','orgao_emissor']]. 'search' = campo de busca.
 * @param {number} props.total - Total de registros
 * @param {number} props.page - Página atual
 * @param {number} props.limit - Itens por página
 * @param {Function} props.onPageChange - (page) => void
 * @param {Function} props.onLimitChange - (limit) => void
 */
const TableFilters = ({
  search = '',
  onSearchChange,
  filters = [],
  filterRows,
  total = 0,
  page = 1,
  limit = 10,
  onPageChange,
  onLimitChange,
  searchPlaceholder = 'Buscar...'
}) => {
  const totalPages = Math.ceil(total / limit) || 1;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const filtersByKey = Object.fromEntries((filters || []).map((f) => [f.key, f]));

  const searchEl = onSearchChange && (
    <div className="search-input-wrap search-input-wrap-fill">
      <FiSearch className="search-icon" />
      <input
        type="text"
        className="search-input"
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
      />
    </div>
  );

  return (
    <div className="table-filters">
      {filterRows ? (
        <>
          {filterRows.map((row, rowIdx) => (
            <div key={rowIdx} className="table-filters-row-full">
              {row.map((key) => {
                if (key === 'search') return <React.Fragment key="search">{searchEl}</React.Fragment>;
                const f = filtersByKey[key];
                return f ? renderFilter(f, true) : null;
              })}
            </div>
          ))}
        </>
      ) : (
        <div className="table-filters-row">
          <div className="table-filters-left">
            {searchEl}
            {filters.map((f) => renderFilter(f, false))}
          </div>
        </div>
      )}
      {(onPageChange || onLimitChange) && total > 0 && (
        <div className="table-filters-row table-filters-pagination-row">
          <div className="table-filters-pagination">
            <div className="pagination-info">
              Mostrando {start}-{end} de {total}
            </div>
            <div className="pagination-controls">
              <select
                value={limit}
                onChange={(e) => onLimitChange?.(parseInt(e.target.value, 10))}
                className="limit-select"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <button
                type="button"
                className="btn-pagination"
                disabled={page <= 1}
                onClick={() => onPageChange?.(page - 1)}
              >
                <FiChevronLeft />
              </button>
              <span className="pagination-page">Página {page} de {totalPages}</span>
              <button
                type="button"
                className="btn-pagination"
                disabled={page >= totalPages}
                onClick={() => onPageChange?.(page + 1)}
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableFilters;
